import { X } from 'lucide-react';
import { Fragment } from 'react';
import type { ScenarioRow, ScenarioStatusFilter } from '../types.js';
import { EMPTY_FILTER, type ScenarioFilterValue } from './ScenarioFilterDrawer.js';
import { FOLDER_ALL, FOLDER_UNCATEGORIZED, TEAM_ALL } from './ScenarioPageHeader.js';

/**
 * Active-filter chip strip rendered between the toolbar and the
 * scenarios table. Each chip explains one active narrowing and has
 * an inline `✕` to drop just that constraint; a trailing
 * `Clear all` resets everything. Stays hidden when nothing's active
 * so the table jumps the same way the legacy did.
 *
 * Filters surfaced (the visible-state set):
 *   • free-text search
 *   • folder dropdown (`All scenarios` is the no-op default)
 *   • status pills (`all` is the no-op default)
 *   • power-drawer: modules range, ops range, modifiedBy[], apps[], tags[]
 */
export interface ActiveFilterStripProps {
  search: string;
  onSearchClear: () => void;
  folder: string;
  onFolderClear: () => void;
  status: ScenarioStatusFilter;
  onStatusClear: () => void;
  team: string;
  onTeamClear: () => void;
  /** Team `fusionId` → display name. Same map as the page header's
   *  `teamOptions` so chip text matches the dropdown label. */
  teamNamesByFusionId: ReadonlyMap<string, string>;
  filter: ScenarioFilterValue;
  onFilterChange: (next: ScenarioFilterValue) => void;
  /** All rows (post-license, pre-filter). Used to map tag IDs back to
   *  their current title for the chip label. */
  rows: ReadonlyArray<ScenarioRow>;
  onClearAll: () => void;
}

interface ChipDef {
  key: string;
  label: string;
  onRemove: () => void;
}

export function ActiveFilterStrip(props: ActiveFilterStripProps): JSX.Element | null {
  const chips = buildChips(props);
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-foreground/10 bg-foreground/[0.015] px-6 py-2 text-xs">
      <span className="text-foreground/40">Filters:</span>
      {chips.map((c) => (
        <Fragment key={c.key}>
          <button
            type="button"
            onClick={c.onRemove}
            className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--brand-yellow)]/60 bg-[color:var(--brand-yellow)]/10 px-2.5 py-0.5 text-foreground/90 transition-colors hover:bg-[color:var(--brand-yellow)]/20"
          >
            <span>{c.label}</span>
            <X className="h-3 w-3 text-foreground/60" />
          </button>
        </Fragment>
      ))}
      <button
        type="button"
        onClick={props.onClearAll}
        className="ml-1 text-foreground/50 underline-offset-2 hover:text-foreground hover:underline"
      >
        Clear all
      </button>
    </div>
  );
}

function buildChips(p: ActiveFilterStripProps): ReadonlyArray<ChipDef> {
  const out: ChipDef[] = [];
  if (p.search.trim()) {
    out.push({
      key: 'search',
      label: `Search: "${truncate(p.search.trim(), 24)}"`,
      onRemove: p.onSearchClear,
    });
  }
  if (p.status !== 'all') {
    out.push({
      key: 'status',
      label: `Status: ${p.status === 'active' ? 'Active' : 'Inactive'}`,
      onRemove: p.onStatusClear,
    });
  }
  if (p.folder !== FOLDER_ALL) {
    const folderLabel = p.folder === FOLDER_UNCATEGORIZED ? 'Uncategorized' : p.folder;
    out.push({
      key: 'folder',
      label: `Folder: ${folderLabel}`,
      onRemove: p.onFolderClear,
    });
  }
  if (p.team !== TEAM_ALL) {
    const teamLabel = p.teamNamesByFusionId.get(p.team) ?? `Team ${p.team}`;
    out.push({
      key: 'team',
      label: `Team: ${teamLabel}`,
      onRemove: p.onTeamClear,
    });
  }
  if (p.filter.modules) {
    out.push({
      key: 'modules',
      label: `Modules ${p.filter.modules[0]}–${p.filter.modules[1]}`,
      onRemove: () => p.onFilterChange({ ...p.filter, modules: null }),
    });
  }
  if (p.filter.operations) {
    out.push({
      key: 'operations',
      label: `Ops ${p.filter.operations[0]}–${p.filter.operations[1]}`,
      onRemove: () => p.onFilterChange({ ...p.filter, operations: null }),
    });
  }
  for (const user of p.filter.modifiedBy) {
    out.push({
      key: `modifiedBy:${user}`,
      label: `Modified by ${user}`,
      onRemove: () =>
        p.onFilterChange({
          ...p.filter,
          modifiedBy: p.filter.modifiedBy.filter((u) => u !== user),
        }),
    });
  }
  for (const app of p.filter.apps) {
    out.push({
      key: `app:${app}`,
      label: `App: ${app}`,
      onRemove: () =>
        p.onFilterChange({
          ...p.filter,
          apps: p.filter.apps.filter((a) => a !== app),
        }),
    });
  }
  // Tag chips: resolve current title from rows so a stale filter
  // value that still carries titles (legacy) doesn't show as the ID.
  const tagTitleById = new Map<string, string>();
  for (const row of p.rows) {
    for (const t of row.tags ?? []) {
      if (typeof t.id === 'number' && t.title) {
        tagTitleById.set(String(t.id), t.title);
      }
    }
  }
  for (const tagKey of p.filter.tags) {
    const title = tagTitleById.get(tagKey) ?? tagKey;
    out.push({
      key: `tag:${tagKey}`,
      label: `Tag: ${title}`,
      onRemove: () =>
        p.onFilterChange({
          ...p.filter,
          tags: p.filter.tags.filter((t) => t !== tagKey),
        }),
    });
  }
  return out;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

// Re-export so the page can `import { EMPTY_FILTER } from
// './ActiveFilterStrip.js'` without crossing two component files.
export { EMPTY_FILTER };
